import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import customEmailService from '../services/customEmailService';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { getFriendlyErrorMessage } from '../utils/friendlyError';

const EmailVerification = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Check if user is already verified
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // If user is already verified, redirect to appropriate destination
    if (currentUser.emailVerified) {
      // Check if there's a saved redirect path
      const redirectPath = localStorage.getItem('redirectAfterVerification');
      if (redirectPath) {
        localStorage.removeItem('redirectAfterVerification'); // Clear it after use
        navigate(redirectPath);
      } else {
        // Default redirect based on user type
        const userType = localStorage.getItem('userType') || 'influencer';
        navigate(`/${userType}`);
      }
    }

    // Set up interval to check verification status
    const checkVerificationStatus = setInterval(async () => {
      if (currentUser) {
        try {
          // Refresh token before checking verification status
          await currentUser.getIdToken(true);
          await currentUser.reload();
          
          if (currentUser.emailVerified) {
            clearInterval(checkVerificationStatus);
            setSuccess(true);
            
            // Redirect after showing success message
            setTimeout(() => {
              // Check if there's a saved redirect path
              const redirectPath = localStorage.getItem('redirectAfterVerification');
              if (redirectPath) {
                localStorage.removeItem('redirectAfterVerification'); // Clear it after use
                navigate(redirectPath);
              } else {
                // Default redirect based on user type
                const userType = localStorage.getItem('userType') || 'influencer';
                navigate(`/${userType}`);
              }
            }, 3000);
          }
        } catch (error) {
          console.error("Error reloading user:", error);
          // If token is expired, user might need to log in again
          if (error.code === 'auth/user-token-expired') {
            console.log("User token expired, redirecting to login");
            clearInterval(checkVerificationStatus);
            navigate('/login');
          }
        }
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(checkVerificationStatus);
  }, [currentUser, navigate]);

  // Handle countdown for resend button
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setLoading(true);
      setError('');
      
      if (currentUser) {
        // Use custom email service to completely bypass Firebase's email system
        const emailResult = await customEmailService.sendEmailVerification(currentUser);
        
        if (!emailResult.success) {
          setError(emailResult.error || 'Failed to send verification email');
          setResendCooldown(60); // Standard cooldown
          return;
        }
        
        setSuccess(true);
        setResendCooldown(60); // 60 seconds cooldown
      }
    } catch (error) {
      console.error("Error sending verification email:", error);
      setError(getFriendlyErrorMessage(error, "Failed to send verification email. Please try again later."));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Error logging out:", error);
      setError(getFriendlyErrorMessage(error, "Failed to log out. Please try again."));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 relative overflow-hidden">
      <Header />
      
      {/* Background effects */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div 
          className="absolute rounded-full bg-primary/5 w-96 h-96 top-20 -left-48"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute rounded-full bg-blue-200/10 w-96 h-96 top-1/3 -right-20"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ 
            duration: 6, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </div>
      
      <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative z-10 mt-16">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <Mail className="h-16 w-16 text-primary" />
          </div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please check your inbox and verify your email address
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-6">
                <div className="flex justify-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            {success && (
              <div className="rounded-md bg-green-50 p-4 mb-6">
                <div className="flex justify-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      {currentUser?.emailVerified 
                        ? "Your email has been verified! Redirecting you to your dashboard..." 
                        : "Verification email sent successfully!"}
                    </h3>
                  </div>
                </div>
              </div>
            )}
            
            <div className="text-center mx-auto max-w-md">
              <p className="text-gray-700 mb-6" data-component-name="EmailVerification">
                We've sent a verification email to <span className="font-medium">{currentUser?.email}</span>.
                <br />
                Please check your inbox and click the verification link.
              </p>
              
              <p className="text-gray-600 mb-8 text-sm">
                If you don't see the email, check your spam folder or request a new verification link.
              </p>
              
              <div className="space-y-4 mx-auto">
                <button
                  onClick={handleResendVerification}
                  disabled={loading || resendCooldown > 0}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${(loading || resendCooldown > 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="xs" color="white" />
                      <span className="ml-2">Sending...</span>
                    </>
                  ) : resendCooldown > 0 ? (
                    `Resend in ${resendCooldown}s`
                  ) : (
                    'Resend Verification Email'
                  )}
                </button>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EmailVerification;
